import { Box, Divider, Stack, SxProps, Theme, useTheme } from '@mui/material';

import { CinemasIcon } from './cinemas-icon';
import { ContactUsIcon } from './contact-us-icon';
import { ExperiencesIcon } from './experiences-icon';
import { FC } from 'react';
import FooterLink from './footer-link';
import Link from 'next/link';

import { OfferIcon } from './offer-icon';
import { PATHS } from '@/constants/routes';
import { PrivacyPolicyIcon } from './privacy-policy';
import { PrivateBookingIcon } from './private-booking-icon';
import SocialIcon from './social-icon';
import StoreBadge from './store-badge';
import { TermsAndConditionsIcon } from './terms-condeition-icon';
import { VouchersIcon } from './vouchers-icon';
import { pxToRem } from '@/theme/typography';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import ImageBoxFuture from '../shared/image-box-future';

const LogoFacebook = '/icons/footer/facebook.svg';
const LogoInstagram = '/icons/footer/instagram.svg';
const LogoLinkedin = '/icons/footer/linkedin.svg';
const LogoTiktok = '/icons/footer/tiktok.svg';
const LogoTwitter = '/icons/footer/twitter.svg';

const BadgeAppStore = '/icons/footer/muvi_AppStore_White.svg';
const BadgeAppStoreAr = '/icons/footer/muvi_AppStore_White_AR.svg';
const BadgeGooglePlay = '/icons/footer/muvi_GooglePlay_White.svg';
const BadgeGooglePlayAr = '/icons/footer/muvi_GooglePlay_White_AR.svg';

const LINK_APP_STORE = 'https://apps.apple.com/sa/app/muvi-cinemas/id1482746718';
const LINK_GOOGLE_PLAY = 'https://play.google.com/store/apps/details?id=com.muvi';

export const Footer: FC = () => {
  const theme = useTheme();
  const router = useRouter();
  const intl = useIntl();

  // The badge artwork has its wording baked into the vector paths, so the
  // Arabic version is a separate asset rather than a translated string.
  const isArabic = router.locale === 'ar';

  const sx: Partial<SxProps<Theme>> = {
    backgroundColor: theme.palette.primary.main,
    position: 'relative',
    left: 0,
    bottom: 0,
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'end',
    paddingTop: '100px',
    paddingBlockEnd: '30px',
    overflow: 'hidden',
    marginTop: 'auto',
  };

  const sxContainer: Partial<SxProps<Theme>> = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    [theme.breakpoints.up('desktop')]: {
      flexDirection: 'row',
      paddingBlockStart: '3rem',
      width: '60%',
      alignItems: 'center',
    },
  };

  const sxLogoAndSocialContainer: Partial<SxProps<Theme>> = {
    marginBottom: '40px',
    [theme.breakpoints.up('desktop')]: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      marginBottom: '0px',
    },
  };

  const sxLogoAndSocial: Partial<SxProps<Theme>> = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.up('desktop')]: {
      alignItems: 'start',
    },
  };

  const sxLinksStackContainer: Partial<SxProps<Theme>> = {
    flexDirection: 'column',
    // Logical alignment: start is left under `dir='ltr'` and right under
    // `dir='rtl'`, so English and Arabic each align without a locale branch.
    textAlign: 'start',
    alignItems: 'flex-start',
    [theme.breakpoints.up('desktop')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
  };

  const sxDivider: Partial<SxProps<Theme>> = {
    display: 'none',
    [theme.breakpoints.up('desktop')]: {
      display: 'block',
    },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: (
          [
            PATHS.MOVIES_SLUG_BOOKING,
            PATHS.BOOKINGS_ID_CHECKOUT,
            PATHS.UNDER_MAINTENANCE,
          ] as string[]
        ).includes(router.pathname)
          ? 'none'
          : 'block',
        marginTop: 'auto',
      }}
    >
      <Box
        sx={{
          width: '100%',
          overflow: 'hidden',
          height: '75px',
          position: 'absolute',
          top: '-1px',

          '&:before': {
            content: '""',
            position: 'absolute',
            top: '-90px',
            left: '50%',
            transform: 'translateX(-50%)',
            height: '150px',
            width: '120%',
            borderRadius: '100%',
            background: theme.palette.layout.backgroundColor,
            WebkitBoxShadow: '0 -40px 40px -40px rgba(0, 0, 0, 0.25) inset',
            boxShadow: '0 -40px 40px -40px  rgba(0, 0, 0, 0.25) inset',
            [theme.breakpoints.up('desktop')]: {
              paddingBottom: '50px',
              top: '-26px',
              height: '100px',
            },
            zIndex: 10,
          },
        }}
      />
      <Box component='footer' sx={sx}>
        <Box sx={sxContainer}>
          <Box sx={sxLogoAndSocialContainer}>
            <Box sx={sxLogoAndSocial}>
              <Link href={{ pathname: PATHS.HOME }} legacyBehavior passHref>
                <Box
                  component='a'
                  sx={{ display: 'flex' }}
                  aria-label={intl.formatMessage({
                    id: 'images.title.logo',
                  })}
                >
                  {/*
                    Decorative: the anchor carries the accessible name, so a
                    title here would have screen readers announce it twice.
                  */}
                  <ImageBoxFuture
                    img='/icons/logos/muvi-logo-gray.svg'
                    title=''
                    objectFit='contain'
                    sx={{
                      width: pxToRem(150),
                      height: pxToRem(95),
                    }}
                  />
                </Box>
              </Link>

              <Stack direction='row' spacing='16px' sx={{ mt: 3 }}>
                <StoreBadge
                  logo={isArabic ? BadgeAppStoreAr : BadgeAppStore}
                  path={LINK_APP_STORE}
                  name='App Store'
                />
                <StoreBadge
                  logo={isArabic ? BadgeGooglePlayAr : BadgeGooglePlay}
                  path={LINK_GOOGLE_PLAY}
                  name='Google Play'
                />
              </Stack>

              <Stack direction='row' spacing='16px' sx={{ mt: 3 }}>
                <SocialIcon
                  logo={LogoTwitter}
                  path='https://twitter.com/muvicinemas'
                  name='Twitter'
                />
                <SocialIcon
                  logo={LogoInstagram}
                  path='https://www.instagram.com/muvi_cinemas/'
                  name='Instagram'
                />
                <SocialIcon
                  logo={LogoFacebook}
                  path='https://www.facebook.com/muvicinemas/'
                  name='Facebook'
                />
                <SocialIcon
                  logo={LogoLinkedin}
                  path='https://www.linkedin.com/company/muvi-cinemas'
                  name='LinkedIn'
                />
                <SocialIcon
                  logo={LogoTiktok}
                  path='https://www.tiktok.com/@muvicinemas'
                  name='TikTok'
                />
              </Stack>
            </Box>
          </Box>
          <Box sx={sxDivider}>
            <Divider
              orientation='vertical'
              flexItem
              sx={{
                backgroundColor: 'white',
                height: '200px',
              }}
            />
          </Box>
          <Stack
            component='nav'
            direction='row'
            sx={sxLinksStackContainer}
            spacing={{ desktop: 8 }}
          >
            <Stack component='ul'>
              <FooterLink
                icon={color => <CinemasIcon color={color} />}
                text='footer.link.cinemas'
                path='cinemas'
              />
              <FooterLink
                icon={color => <ContactUsIcon color={color} />}
                text='footer.link.contact.us'
                path='contact-us'
              />
              <FooterLink
                icon={color => <OfferIcon color={color} />}
                text='footer.link.offers'
                path='offers'
              />

              <FooterLink
                icon={color => <PrivacyPolicyIcon color={color} />}
                text='footer.link.privacy.policy'
                path='privacy-policy'
              />
              <FooterLink
                icon={color => <VouchersIcon color={color} />}
                text='footer.link.vouchers'
                path='voucher-redemption'
              />
            </Stack>
            <Stack component='ul'>
              <FooterLink
                icon={color => <PrivateBookingIcon color={color} />}
                text='footer.link.private.booking'
                path='private-booking'
              />
              <FooterLink
                icon={color => <TermsAndConditionsIcon color={color} />}
                text='footer.link.terms.and.conditions'
                path='terms-and-conditions'
              />
              <FooterLink
                icon={color => <ExperiencesIcon color={color} />}
                text='footer.link.experiences'
                path='experiences'
              />
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};
